import { useCallback, useContext, useState } from 'react';
import { Button } from '../../../SubComponents/Buttons';
import { bookingState } from '../client-state/booking-state-context';
import useBookingChangeStep from '../../../Hooks/use-booking-change-step';
import { bookingStepDependencies } from '..';
import { graphqlSdk } from '../../../Utils/graphql-client';
import { loadingReducerActionTypes } from '../../../redusers/loading-reducer';
import { bookingActionTypes } from '../client-state/reducers/bookingReducer';
import { orderReducerActionTypes } from '../../../redusers/order-reducer';
import { paymentReducerActionTypes } from '../../../redusers/payment-reducer';
import useBookingTracking from 'Hooks/useBookingTracking';
import DialogModal, { DialogModalType } from 'ui-components/dialog';
import {
  ErrorLocalization,
  LocalizationCategory,
  ProductLocalization,
} from 'localizations/types';
import { AddonsErrorCode } from 'Utils/graphql-sdk';
import useBookingShop from '../server-data/use-booking-shop';
import useTypeSafeLocalize from 'Hooks/use-type-safe-localize';
import { Label } from 'SubComponents/Text';

const checkErrorCode = (error: unknown, errorCode: AddonsErrorCode) => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'errors' in error.response &&
    Array.isArray(error.response.errors) &&
    error.response.errors[0]?.extensions?.code === errorCode
  ) {
    return true;
  } else {
    return false;
  }
};

const getRentalItemErrorDetails = (error: unknown) => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'errors' in error.response
  ) {
    const errors = error.response.errors;

    if (Array.isArray(errors) && errors.length > 0) {
      const firstError = errors[0];
      const extensions = firstError.extensions;

      if (extensions && typeof extensions === 'object') {
        return {
          itemId: extensions.itemId,
          itemsAvailable: extensions.itemsAvailable,
          itemsRequested: extensions.itemsRequested,
          message: firstError.message,
        };
      }
    }
  }
};

const ReserveBookingButtonWithData = () => {
  const { data: shop } = useBookingShop();
  const { state, dispatch } = useContext(bookingState);
  const localize = useTypeSafeLocalize();
  const changeStep = useBookingChangeStep(bookingStepDependencies);
  const track = useBookingTracking();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState('');
  const [reservationError, setReservationError] = useState(false);

  const getRentalItemAvailabilityErrorMessage = useCallback(
    (error: unknown) => {
      const errorDetails = getRentalItemErrorDetails(error);

      if (errorDetails) {
        const item = shop?.items.find((item) => item.id === errorDetails.itemId);

        if (item) {
          return `${item.name}${localize(
            LocalizationCategory.error,
            ErrorLocalization.RentalItemUnavailable
          )}`;
        }
      }

      return localize(
        LocalizationCategory.error,
        ErrorLocalization.FallbackRentalItemUnavailable
      );
    },
    [shop?.items, localize]
  );

  const updateBooking = async (options?: { forfeitRentalItems: boolean }) => {
    if (state.booking.id && state.booking.date) {
      try {
        dispatch({ type: loadingReducerActionTypes.BOOKING, payload: true });
        const response = await graphqlSdk.booking_editBooking({
          boatCount: state.booking.boatCount,
          duration: state.booking.duration,
          date: state.booking.date,
          id: state.booking.id,
          pickupLocationId: state.booking.pickupLocationId,
          dropoffLocationId: state.booking.dropoffLocationId,
          forfeitRentalItems: options?.forfeitRentalItems ?? false,
        });

        dispatch({
          type: bookingActionTypes.UPDATE_BOOKING,
          payload: {
            ...response.editBooking,
            lang: response.editBooking.lang ?? state.booking.lang,
          },
        });
        // if not using legacy payments, filter out items that are not valid for edited booking
        dispatch({
          type: orderReducerActionTypes.UPDATE_ORDER,
          payload: response.editBooking.addons.map((addon) => {
            return {
              quantity: addon.quantity,
              itemId: addon.itemId,
            };
          }),
        });
        /* TODO: Fix the any type  */
      } catch (error: any) {
        if (error?.response?.errors?.[0]?.extensions?.code === 'nothing-changed') {
          return dispatch({ type: bookingActionTypes.UPDATE_BOOKING, payload: {} });
        } else {
          throw error;
        }
      }
    }
  };

  const createPaymentIntent = async (bookingId: string) => {
    const paymentIntentResponse = await graphqlSdk.booking_createBookingPaymentIntent({
      bookingId: bookingId,
    });

    return dispatch({
      type: paymentReducerActionTypes.UPDATE_CLIENT_SECRET,
      payload: paymentIntentResponse.createBookingPaymentIntent,
    });
  };

  const createBooking = async () => {
    if (!state.booking.customerId && state.booking.date && state.booking.shopId) {
      try {
        dispatch({ type: loadingReducerActionTypes.BOOKING, payload: true });
        const response = await graphqlSdk.booking_reserve({
          date: state.booking.date,
          duration: state.booking.duration,
          boatCount: state.booking.boatCount,
          pickupLocationId: state.booking.pickupLocationId,
          dropoffLocationId: state.booking.dropoffLocationId,
          includeWarranty: state.isWarrantyApplied,
          shopId: state.booking.shopId,
          lang: state.booking.lang,
          customer: {
            firstname: state.customer.customer.firstname,
            lastname: state.customer.customer.lastname,
            email: state.customer.customer.email,
            phone: state.customer.customer.phone,
            address: state.customer.customer.address,
          },
        });

        // create payment intent, keep payment intent in state
        // don't await it though
        createPaymentIntent(response.reserve.id);

        return dispatch({
          type: bookingActionTypes.UPDATE_BOOKING,
          payload: {
            ...response.reserve,
            lang: response.reserve.lang ?? state.booking.lang,
          },
        });
      } catch (error) {
        throw error;
      }
    }
  };

  return (
    <>
      <Button
        id="step2NextButton"
        loading={state.loading.booking}
        onClick={async () => {
          if (state.booking.date) {
            try {
              if (!state.booking.customerId) {
                await createBooking();
              } else if (state.booking.id) {
                await updateBooking();
              }

              changeStep(3);

              track({ type: 'GO_TO_EXTRAS_PAGE' });
            } catch (error) {
              dispatch({ type: loadingReducerActionTypes.BOOKING, payload: false });

              // Show rental item dialog
              if (checkErrorCode(error, AddonsErrorCode.RentalItemAvailability)) {
                console.error(error);
                setDialogContent(getRentalItemAvailabilityErrorMessage(error));
                setIsDialogOpen(true);
                dispatch({ type: bookingActionTypes.UPDATE_BOOKING, payload: {} });
                return;
              } else {
                console.error(error);
                setReservationError(true);
                dispatch({ type: bookingActionTypes.UPDATE_BOOKING, payload: {} });
                return;
              }
            }
          }
        }}
        className={!state.booking.date && 'disabled'}
        look="secondary"
        size="big"
        style={{ alignSelf: 'center' }}
      >
        {localize(LocalizationCategory.product, ProductLocalization.next)}
      </Button>

      {reservationError && (
        <Label error ml="25px" mt="3">
          {localize(LocalizationCategory.error, ErrorLocalization.UnknownError)}
        </Label>
      )}

      <DialogModal
        open={isDialogOpen}
        setOpen={setIsDialogOpen}
        type={DialogModalType.ERROR}
        title={localize(
          LocalizationCategory.error,
          ErrorLocalization.RentalItemUnavailableTitle
        )}
        content={dialogContent}
        confirmText={localize(
          LocalizationCategory.product,
          ProductLocalization.moveBooking
        )}
        onConfirm={async () => {
          try {
            setIsDialogOpen(false);
            await updateBooking({
              forfeitRentalItems: true,
            });
            changeStep(3);
          } catch (error) {
            console.error(error);
            setReservationError(true);
            dispatch({ type: bookingActionTypes.UPDATE_BOOKING, payload: {} });
          }
        }}
        cancelText={localize(LocalizationCategory.product, ProductLocalization.keepAddon)}
        onCancel={() => {
          setIsDialogOpen(false);
        }}
      />
    </>
  );
};

export default ReserveBookingButtonWithData;
